if (!customElements.get("personalized-dog-picker")) {
  customElements.define(
    "personalized-dog-picker",
    class PersonalizedDogPicker extends HTMLElement {
      constructor() {
        super();

        this.min = parseInt(this.dataset.min, 10) || 1;
        this.max = parseInt(this.dataset.max, 10) || 4;
        this.selections = [];
        this.activeSlot = null;
        this.activeLetter = "all";
        this.messageText = "";
        this.helperText = "Choose at least 1 dog to add this item to cart.";
        this.optionConfirmationDuration = 2000;
        this.optionConfirmationTimers = new Map();
        this.slotConfirmationDuration = 2000;
        this.slotConfirmationTimers = new Map();
        this.browserOpen = false;
        this.hasDocumentKeydownListener = false;
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.handlePresetTriggerClick =
          this.handlePresetTriggerClick.bind(this);
        this.handleStartDesigningClick =
          this.handleStartDesigningClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.cacheElements();
        this.bindEvents();
        this.sync();
        this.filterOptions();
        this.cachePresetSources();
        this.syncPresetAvailability();
        document.addEventListener("click", this.handleStartDesigningClick);
        document.addEventListener("click", this.handlePresetTriggerClick);
        this.bindVariantChanges();
      }

      disconnectedCallback() {
        this.optionConfirmationTimers.forEach((timer) =>
          window.clearTimeout(timer),
        );
        this.optionConfirmationTimers.clear();
        this.slotConfirmationTimers.forEach((timer) =>
          window.clearTimeout(timer),
        );
        this.slotConfirmationTimers.clear();
        this.removeDocumentKeydownListener({ force: true });
        document.removeEventListener("click", this.handleStartDesigningClick);
        document.removeEventListener("click", this.handlePresetTriggerClick);
        this.variantChangeUnsubscriber?.();
        document.body.classList.remove("personalized-dog-picker-browser-open");
        this.browser?.remove();
        this.browserPlaceholder?.remove();
      }

      cacheElements() {
        this.slots = Array.from(this.querySelectorAll("[data-dog-slot]"));
        this.options = Array.from(this.querySelectorAll("[data-dog-option]"));
        this.searchInput = this.querySelector("[data-dog-picker-search]");
        this.letterButtons = Array.from(
          this.querySelectorAll("button[data-dog-letter]"),
        );
        this.resultCount = this.querySelector("[data-dog-picker-result-count]");
        this.emptyState = this.querySelector("[data-dog-picker-empty]");
        this.browser = this.querySelector("[data-dog-picker-browser]");
        this.browserDialog = this.querySelector(
          ".personalized-dog-picker__browser-dialog",
        );
        this.openBrowserButton = this.querySelector("[data-dog-picker-open]");
        this.closeBrowserButtons = Array.from(
          this.querySelectorAll("[data-dog-picker-close]"),
        );
        this.browserSlots = Array.from(
          this.querySelectorAll("[data-dog-browser-slot]"),
        );
        this.message = this.querySelector("[data-dog-picker-message]");
        this.selectionCount = this.querySelector(
          "[data-dog-picker-selection-count]",
        );
        this.publicInputs = Array.from(
          this.querySelectorAll("[data-dog-property]"),
        );
        this.privateTypeInput = this.querySelector(
          "[data-dog-private-property]",
        );
        this.handlesInput = this.querySelector("[data-dog-handles-property]");
        this.idsInput = this.querySelector("[data-dog-ids-property]");
        this.form = document.getElementById(this.dataset.formId);
        this.submitButton = this.form?.querySelector(
          '[type="submit"][name="add"]',
        );
        this.paymentButton = this.form?.querySelector(
          ".shopify-payment-button",
        );
        this.cachePresetSources();

        if (this.submitButton) {
          this.captureNativeSubmitState();
        }
      }

      bindVariantChanges() {
        if (
          typeof subscribe !== "function" ||
          typeof PUB_SUB_EVENTS === "undefined"
        )
          return;

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.handleVariantChange,
        );
      }

      bindEvents() {
        this.options.forEach((option) => {
          option
            .querySelector("button")
            ?.addEventListener("click", () => this.selectOption(option));
        });

        this.slots.forEach((slot, index) => {
          slot
            .querySelector("[data-dog-slot-select]")
            ?.addEventListener("click", () => {
              this.activateSlot(index);
            });

          slot
            .querySelector("[data-dog-slot-remove]")
            ?.addEventListener("click", () => this.removeSelection(index));

          slot.querySelectorAll("[data-dog-slot-move]").forEach((button) => {
            button.addEventListener("click", () =>
              this.moveSelection(index, button.dataset.dogSlotMove),
            );
          });
        });

        this.searchInput?.addEventListener("input", () => this.filterOptions());

        this.openBrowserButton?.addEventListener("click", () =>
          this.openBrowser(),
        );

        this.closeBrowserButtons.forEach((button) => {
          button.addEventListener("click", () => this.closeBrowser());
        });

        this.browserSlots.forEach((slot, index) => {
          slot
            .querySelector("[data-dog-browser-slot-select]")
            ?.addEventListener("click", () => this.activateSlot(index));

          slot
            .querySelector("[data-dog-browser-slot-remove]")
            ?.addEventListener("click", (event) => {
              event.stopPropagation();
              this.removeSelection(index);
            });
        });

        this.letterButtons.forEach((button) => {
          button.addEventListener("click", () => {
            this.activeLetter = button.dataset.dogLetter || "all";
            this.updatePressedState(this.letterButtons, button);
            this.filterOptions();
          });
        });

        this.form?.addEventListener(
          "submit",
          (event) => {
            if (this.isValid()) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.setMessage(
              "Choose at least one dog before adding this item to cart.",
            );
            this.syncSubmitState();
          },
          true,
        );
      }

      selectOption(option) {
        const selection = this.createSelection(option);

        if (this.activeSlot !== null && this.activeSlot < this.max) {
          const updatedSlotIndex =
            this.activeSlot < this.selections.length
              ? this.activeSlot
              : this.selections.length;

          if (this.activeSlot < this.selections.length) {
            this.selections[this.activeSlot] = selection;
          } else if (this.selections.length < this.max) {
            this.selections.push(selection);
          }
          this.activeSlot = null;
          this.setMessage("");
          this.sync();
          this.confirmOption(option);
          this.confirmBrowserSlot(updatedSlotIndex);
          return;
        }

        if (this.selections.length >= this.max) {
          this.setMessage(
            `You can choose up to ${this.max} dogs. Select a filled slot to replace it.`,
          );
          return;
        }

        const updatedSlotIndex = this.selections.length;
        this.selections.push(selection);
        this.setMessage("");
        this.sync();
        this.confirmOption(option);
        this.confirmBrowserSlot(updatedSlotIndex);
      }

      createSelection(option) {
        const { dogId, dogHandle, dogLabel, dogDetail, dogPreviewImage } =
          option.dataset;

        return {
          id: dogId || "",
          handle: dogHandle || "",
          label: dogLabel || "",
          detail: dogDetail || "",
          image: dogPreviewImage || "",
        };
      }

      removeSelection(index) {
        if (index >= this.selections.length) return;

        this.selections.splice(index, 1);
        this.activeSlot = null;
        this.setMessage("");
        this.clearBrowserSlotConfirmations();
        this.sync();
      }

      moveSelection(index, direction) {
        if (index >= this.selections.length) return;

        const targetIndex = direction === "left" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= this.selections.length) return;

        const current = this.selections[index];
        this.selections[index] = this.selections[targetIndex];
        this.selections[targetIndex] = current;
        this.activeSlot = targetIndex;
        this.sync();
      }

      activateSlot(index) {
        const activeIndex =
          index < this.selections.length ? index : this.selections.length;

        this.activeSlot = Math.min(activeIndex, this.max - 1);
        this.setMessage(
          index < this.selections.length
            ? "Choose a dog to replace this slot."
            : `Choose a dog for slot ${this.activeSlot + 1}.`,
        );
        this.renderSlots();
        this.renderBrowserSlots();
        this.openBrowser();
      }

      openBrowser() {
        if (!this.browser || this.browserOpen) return;

        this.previouslyFocusedElement = document.activeElement;
        this.mountBrowser();
        this.browser.hidden = false;
        this.browserOpen = true;
        document.body.classList.add("personalized-dog-picker-browser-open");
        this.addDocumentKeydownListener();

        window.requestAnimationFrame(() => {
          this.browser.classList.add("is-open");
          this.searchInput?.focus();
        });
      }

      handleStartDesigningClick(event) {
        const trigger = event.target.closest("[data-dog-picker-start]");
        if (!trigger) return;

        const targetId = trigger.getAttribute("href")?.replace("#", "");
        if (targetId && targetId !== this.id) return;

        event.preventDefault();

        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        this.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "start",
        });

        window.setTimeout(
          () => this.openBrowser(),
          prefersReducedMotion ? 0 : 240,
        );
      }

      handleVariantChange(event) {
        if (
          event?.data?.sectionId &&
          this.form?.id &&
          !this.form.id.endsWith(event.data.sectionId)
        )
          return;

        this.captureNativeSubmitState();
        this.syncSubmitState();
      }

      closeBrowser() {
        if (!this.browser || !this.browserOpen) return;

        this.browser.classList.remove("is-open");
        this.browserOpen = false;
        document.body.classList.remove("personalized-dog-picker-browser-open");
        this.removeDocumentKeydownListener();
        this.previouslyFocusedElement?.focus?.();
        this.previouslyFocusedElement = null;

        window.setTimeout(() => {
          if (!this.browserOpen && this.browser) {
            this.browser.hidden = true;
          }
        }, 180);
      }

      mountBrowser() {
        if (!this.browser || this.browser.parentElement === document.body)
          return;

        this.browserPlaceholder = document.createComment(
          "personalized-dog-picker-browser",
        );
        this.browser.after(this.browserPlaceholder);
        document.body.append(this.browser);
      }

      handleDocumentKeydown(event) {
        if (event.key === "Escape") {
          if (this.browserOpen) {
            this.closeBrowser();
          }
          return;
        }

        if (event.key !== "Tab") return;

        if (!this.browserOpen) return;

        this.trapFocus(event, this.browserDialog);
      }

      trapFocus(event, container) {
        const focusableElements = this.getFocusableElements(container);
        if (focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
          return;
        }

        if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }

      getFocusableElements(container) {
        if (!container) return [];

        return Array.from(
          container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((element) => element.offsetParent !== null);
      }

      addDocumentKeydownListener() {
        if (this.hasDocumentKeydownListener) return;

        document.addEventListener("keydown", this.handleDocumentKeydown);
        this.hasDocumentKeydownListener = true;
      }

      removeDocumentKeydownListener(options = {}) {
        if (!this.hasDocumentKeydownListener) return;
        if (!options.force && this.browserOpen) return;

        document.removeEventListener("keydown", this.handleDocumentKeydown);
        this.hasDocumentKeydownListener = false;
      }

      cachePresetSources() {
        this.presetSources = Array.from(
          document.querySelectorAll("[data-dog-preset-source]"),
        );
      }

      syncPresetAvailability() {
        this.presetSources.forEach((source) => {
          const preset = this.buildPreset(source);
          const isAvailable = Boolean(preset?.isAvailable);

          source.classList.toggle("is-unavailable", !isAvailable);
          this.getPresetTriggers(preset?.id || "").forEach((trigger) => {
            trigger.disabled = !isAvailable;
            trigger.setAttribute(
              "aria-disabled",
              isAvailable ? "false" : "true",
            );
          });
        });
      }

      getPresetTriggers(presetId) {
        if (!presetId) return [];

        return Array.from(
          document.querySelectorAll("[data-dog-preset-trigger]"),
        ).filter((trigger) => trigger.dataset.dogPresetTarget === presetId);
      }

      handlePresetTriggerClick(event) {
        const trigger = event.target.closest("[data-dog-preset-trigger]");
        if (!trigger) return;

        this.cachePresetSources();
        const presetId = trigger.dataset.dogPresetTarget || "";
        const source = this.presetSources.find(
          (presetSource) => presetSource.dataset.dogPresetId === presetId,
        );
        const preset = this.buildPreset(source);

        if (!preset) return;

        event.preventDefault();

        if (!preset.isAvailable) {
          this.syncPresetAvailability();
          return;
        }

        this.applyPreset(preset);
      }

      buildPreset(source) {
        if (!source) return null;

        const handles = (source.dataset.dogPresetHandles || "")
          .split(",")
          .map((handle) => handle.trim())
          .filter(Boolean)
          .slice(0, this.max);
        const selections = handles
          .map((handle) => {
            const option = this.options.find(
              (dogOption) => dogOption.dataset.dogHandle === handle,
            );

            return option ? this.createSelection(option) : null;
          })
          .filter(Boolean);

        return {
          id: source.dataset.dogPresetId || "",
          selections,
          isAvailable:
            handles.length > 0 && handles.length === selections.length,
        };
      }

      applyPreset(preset) {
        if (!preset?.isAvailable) return;

        this.selections = preset.selections.slice(0, this.max);
        this.activeSlot = null;
        this.setMessage("");
        this.clearBrowserSlotConfirmations();
        this.sync();

        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        this.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "start",
        });
        this.openBrowserButton?.focus?.();
      }

      sync() {
        this.renderSlots();
        this.renderBrowserSlots();
        this.syncInputs();
        this.syncSubmitState();
        this.syncMessage();
      }

      renderSlots() {
        this.slots.forEach((slot, index) => {
          const selection = this.selections[index];
          const imageContainer = slot.querySelector("[data-dog-slot-image]");
          const label = slot.querySelector("[data-dog-slot-label]");
          const detail = slot.querySelector("[data-dog-slot-detail]");
          const leftButton = slot.querySelector('[data-dog-slot-move="left"]');
          const rightButton = slot.querySelector(
            '[data-dog-slot-move="right"]',
          );

          slot.dataset.empty = selection ? "false" : "true";
          slot.classList.toggle("is-active", this.activeSlot === index);

          if (selection) {
            imageContainer.innerHTML = this.renderImage(selection);
            label.textContent = selection.label;
            if (detail) {
              detail.textContent = selection.detail;
              detail.hidden = selection.detail === "";
            }
          } else {
            imageContainer.innerHTML =
              '<span class="personalized-dog-picker__slot-plus">+</span>';
            label.textContent = "Choose dog";
            if (detail) {
              detail.textContent = "";
              detail.hidden = true;
            }
          }

          if (leftButton)
            leftButton.disabled =
              index === 0 || index >= this.selections.length;
          if (rightButton)
            rightButton.disabled = index >= this.selections.length - 1;
        });

        if (this.selectionCount) {
          this.selectionCount.textContent = `${this.selections.length} of ${this.max} selected`;
        }
      }

      renderBrowserSlots() {
        this.browserSlots.forEach((slot, index) => {
          const selection = this.selections[index];
          const selectButton = slot.querySelector(
            "[data-dog-browser-slot-select]",
          );
          const removeButton = slot.querySelector(
            "[data-dog-browser-slot-remove]",
          );
          const imageContainer = slot.querySelector(
            "[data-dog-browser-slot-image]",
          );
          const label = slot.querySelector("[data-dog-browser-slot-label]");
          const formattedSelection = selection
            ? this.formatSelection(selection)
            : "";

          slot.dataset.empty = selection ? "false" : "true";
          slot.classList.toggle("is-active", this.activeSlot === index);
          selectButton?.setAttribute(
            "aria-label",
            selection
              ? `Replace dog ${index + 1}: ${formattedSelection}`
              : `Choose dog ${index + 1}`,
          );

          if (removeButton) {
            removeButton.hidden = !selection;
            removeButton.disabled = !selection;
            removeButton.setAttribute(
              "aria-label",
              selection
                ? `Remove dog ${index + 1}: ${formattedSelection}`
                : `Remove dog ${index + 1}`,
            );
          }

          if (selection) {
            imageContainer.innerHTML = this.renderImage(selection);
            label.textContent = selection.label;
          } else {
            imageContainer.innerHTML =
              '<span class="personalized-dog-picker__browser-lineup-plus">+</span>';
            label.textContent = "";
          }
        });
      }

      syncInputs() {
        this.publicInputs.forEach((input, index) => {
          const selection = this.selections[index];
          const value = selection ? this.formatSelection(selection) : "";

          input.value = value;
          input.disabled = value === "";
        });

        const hasSelections = this.selections.length > 0;
        const handles = this.selections
          .map((selection) => selection.handle)
          .join(",");
        const ids = this.selections.map((selection) => selection.id).join(",");

        if (this.privateTypeInput)
          this.privateTypeInput.disabled = !hasSelections;
        if (this.handlesInput) {
          this.handlesInput.value = handles;
          this.handlesInput.disabled = !hasSelections;
        }
        if (this.idsInput) {
          this.idsInput.value = ids;
          this.idsInput.disabled = !hasSelections;
        }
      }

      confirmOption(option) {
        window.clearTimeout(this.optionConfirmationTimers.get(option));

        option.classList.add("is-confirming");

        const timer = window.setTimeout(() => {
          option.classList.remove("is-confirming");
          this.optionConfirmationTimers.delete(option);
        }, this.optionConfirmationDuration);

        this.optionConfirmationTimers.set(option, timer);
      }

      confirmBrowserSlot(index) {
        const slot = this.browserSlots[index];
        if (!slot) return;

        window.clearTimeout(this.slotConfirmationTimers.get(slot));
        slot.classList.add("is-updated");

        const timer = window.setTimeout(() => {
          slot.classList.remove("is-updated");
          this.slotConfirmationTimers.delete(slot);
        }, this.slotConfirmationDuration);

        this.slotConfirmationTimers.set(slot, timer);
      }

      clearBrowserSlotConfirmations() {
        this.slotConfirmationTimers.forEach((timer, slot) => {
          window.clearTimeout(timer);
          slot.classList.remove("is-updated");
        });
        this.slotConfirmationTimers.clear();
      }

      syncSubmitState() {
        if (!this.submitButton) return;

        const isValid = this.isValid();
        const nativeDisabled =
          this.submitButton.dataset.personalizedNativeDisabled === "true";

        if (!isValid) {
          if (this.submitButton.dataset.personalizedDisabled !== "true") {
            this.captureNativeSubmitState();
          }

          this.submitButton.disabled = true;
          this.submitButton.dataset.personalizedDisabled = "true";
          if (this.paymentButton) this.paymentButton.hidden = true;
          return;
        }

        if (
          !nativeDisabled &&
          this.submitButton.dataset.personalizedDisabled === "true"
        ) {
          this.submitButton.disabled = false;
        }
        delete this.submitButton.dataset.personalizedDisabled;

        if (this.paymentButton)
          this.paymentButton.hidden = this.submitButton.disabled;
      }

      captureNativeSubmitState() {
        if (!this.submitButton) return;

        this.submitButton.dataset.personalizedNativeDisabled = this.submitButton
          .disabled
          ? "true"
          : "false";
      }

      filterOptions() {
        const query = this.normalize(this.searchInput?.value || "");
        let visibleCount = 0;

        this.options.forEach((option) => {
          const search = this.normalize(option.dataset.dogSearch || "");
          const letter = this.normalize(option.dataset.dogLetter || "");
          const matchesSearch = query === "" || search.includes(query);
          const matchesLetter =
            this.activeLetter === "all" || letter === this.activeLetter;
          const isVisible = matchesSearch && matchesLetter;

          option.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        if (this.resultCount) {
          this.resultCount.textContent = `${visibleCount} ${visibleCount === 1 ? "breed" : "breeds"}`;
        }

        if (this.emptyState) {
          this.emptyState.hidden = visibleCount !== 0;
        }
      }

      updatePressedState(buttons, activeButton) {
        buttons.forEach((button) => {
          const isActive = button === activeButton;
          button.classList.toggle("is-active", isActive);
          button.setAttribute("aria-pressed", isActive ? "true" : "false");
        });
      }

      renderImage(selection) {
        if (!selection.image) {
          const initial = (selection.label || "?").slice(0, 1).toUpperCase();
          return `<span class="personalized-dog-picker__option-placeholder">${this.escapeHtml(initial)}</span>`;
        }

        return `<img src="${this.escapeHtml(selection.image)}" alt="${this.escapeHtml(this.formatSelection(selection))}" loading="lazy">`;
      }

      formatSelection(selection) {
        if (!selection.detail) return selection.label;
        return `${selection.label} - ${selection.detail}`;
      }

      isValid() {
        return this.selections.length >= this.min;
      }

      setMessage(message) {
        this.messageText = message;
        this.syncMessage();
      }

      syncMessage() {
        if (!this.message) return;
        this.message.textContent =
          this.messageText || (!this.isValid() ? this.helperText : "");
      }

      normalize(value) {
        return value
          .toString()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim()
          .toLowerCase();
      }

      escapeHtml(value) {
        return value.toString().replace(/[&<>"']/g, (character) => {
          return {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#039;",
          }[character];
        });
      }
    },
  );
}
